<script setup>
import { useTeletelFeatures } from 'dashboard/composables/useTeletelFeatures';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import ResolveAction from 'dashboard/components/buttons/ResolveAction.vue';
import Avatar from 'next/avatar/Avatar.vue';
import ConversationSlaControl from './components/ConversationSlaControl.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import TeletelFlowRunBanner from './TeletelFlowRunBanner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useI18n } from 'vue-i18n';
import { HEADER_TEXT_CONTROL_CLASS } from 'dashboard/helper/conversationHeaderStyles';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import ConversationAction from 'dashboard/routes/dashboard/conversation/ConversationAction.vue';
import LifecycleStageSelector from 'dashboard/components-next/teletel/lifecycle/LifecycleStageSelector.vue';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { isTeletelFeatureEnabled } = useTeletelFeatures();

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

watch(
  () => isTeletelFeatureEnabled('sla'),
  enabled => {
    if (enabled) store.dispatch('sla/get');
  },
  { immediate: true }
);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

// The divider is only drawn when the information group actually rendered
// something; otherwise it would sit at the edge of the row on its own.
const hasHeaderInfoGroup = computed(
  () =>
    Boolean(props.chat?.sla_policy_id) ||
    Boolean(isTeletelFeatureEnabled('sla')) ||
    Boolean(
      store.getters['teletelFlows/getConversationRun'](currentChat.value?.id)
    )
);

const assignee = computed(() => currentChat.value?.meta?.assignee || null);
const assigneeTooltip = computed(() =>
  assignee.value
    ? t('CONVERSATION.HEADER.ASSIGNEE', { name: assignee.value.name })
    : t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')
);

const { uiSettings, updateUISettings } = useUISettings();
const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open
);
const isCopilotPanelOpen = computed(
  () => uiSettings.value.is_copilot_panel_open
);

const handleContactToggle = () => {
  if (isCopilotPanelOpen.value) {
    updateUISettings({
      is_contact_sidebar_open: true,
      is_copilot_panel_open: false,
    });
  } else {
    updateUISettings({
      is_contact_sidebar_open: !isContactSidebarOpen.value,
      is_copilot_panel_open: false,
    });
  }
};

const keyboardEvents = {
  'Alt+KeyO': {
    action: handleContactToggle,
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 py-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 overflow-hidden xl:flex-1 xl:min-w-72"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
      />
      <div
        class="flex flex-1 flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex w-full min-w-0 flex-row items-center gap-1 p-0 m-0">
          <span
            class="min-w-0 flex-1 truncate text-sm font-medium leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
          <LifecycleStageSelector
            v-if="currentContact.id"
            :contact-id="currentContact.id"
            :conversation-id="currentChat.id"
            :show-history="false"
            class="flex-shrink-0"
            header
          />
        </div>

        <div
          v-if="isSnoozed"
          class="flex items-center gap-1 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
        >
          <span class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end min-w-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <!-- What can be done to it. -->
      <div class="flex flex-row items-center gap-2 flex-shrink-0">
        <Popover align="end">
          <template #default="{ isOpen }">
            <!-- Who owns this conversation is the first thing an agent looks
                 for, so the assignment control says it instead of hiding it
                 behind a generic icon. It opens the same actions panel. -->
            <NextButton
              v-tooltip.bottom="assigneeTooltip"
              ghost
              slate
              sm
              class="max-w-40 gap-1.5 px-2 text-xs font-medium"
              :class="[
                HEADER_TEXT_CONTROL_CLASS,
                { '!bg-n-alpha-2 text-n-slate-12': isOpen },
              ]"
            >
              <Avatar
                v-if="assignee"
                :name="assignee.name"
                :src="assignee.thumbnail"
                :size="18"
                hide-offline-status
              />
              <span v-else class="i-lucide-user-plus size-4 flex-shrink-0" />
              <span class="min-w-0 truncate">
                {{
                  assignee
                    ? assignee.name
                    : $t('CONVERSATION.HEADER.UNASSIGNED')
                }}
              </span>
            </NextButton>
          </template>
          <template #content>
            <div class="p-3 w-80 max-h-[75vh] overflow-y-auto">
              <div
                class="flex items-center justify-between pb-2 mb-2 border-b border-n-weak"
              >
                <span class="text-sm font-semibold text-n-slate-12">
                  {{
                    $t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')
                  }}
                </span>
              </div>
              <ConversationAction
                :conversation-id="currentChat.id"
                :inbox-id="currentChat.inbox_id"
              />
            </div>
          </template>
        </Popover>
        <!-- The call button owns its styling: it renders a fragment root, so a
             class passed from here is silently dropped by Vue. -->
        <ConversationCallButton :inbox="inbox" :chat="currentChat" />
        <ResolveAction
          :conversation-id="currentChat.id"
          :status="currentChat.status"
          separate-actions
        />
      </div>
      <span
        v-if="hasHeaderInfoGroup"
        class="h-5 w-px flex-shrink-0 bg-n-slate-6"
        aria-hidden="true"
      />
      <!-- What this conversation is: the SLA it runs under and the flow on
           it. Read, not pressed - kept apart from the action buttons. -->
      <div class="flex flex-row items-center gap-2 min-w-0 overflow-hidden">
        <ConversationSlaControl :chat="chat" :parent-width="width" />
        <TeletelFlowRunBanner :conversation-id="currentChat.id" />
      </div>
    </div>
  </div>
</template>
